import { computed, reactive, readonly, toRefs } from 'vue'
import { createOrderApi, orderApi, addToBasketApi, cancelOrderApi, adminBlockTimeApi } from '@/api'
import { setGlobalMessage } from '@/stores/Global'
import {amountServices, cateringServices, selectServices} from '@/stores/Services'
import {
  selectedRoom,
  selectedTimeCode,
  weeks,
  setSelectedRoomId,
  setSelectedTimeCode,
} from '@/stores/Rooms'
import { paymentType, setUserDetailValues } from '@/stores/UserDetails'
import {ecommerceAddToCart, ecommerceRemoveFromCart} from '@/utils/analytics'

const state = reactive({
  order: [],
  orderHash: window.room_rent.hash,
  orderServices: [],
  paidServices: [],
  isEditMode: false,
  isCancelRequested: false,
  amountServiceMinPrice: 0,
  useYourOwnAmountServicePrice: 0,
  useYourOwnAmountServiceHash: 0,
  isUseYourOwnAmountService: false,
  isUseYourOwnAmountServiceDescription: '',
  useYourOwnAmountServiceAlreadyPaid: false,
  canSelectUseYourOwnAmountService: false,
  isAdmin: false,
  canSelectCatering: false,
  canEdit: false,
})

export async function createOrder() {
  let selectedWeek = null

  weeks.value.forEach((week) => {
    if (week.selected) {
      selectedWeek = week.id
    }
  })

  if (
    !selectedRoom.value ||
    !selectedRoom.value.hash ||
    !selectedWeek ||
    !selectedTimeCode.value
  ) {
    return false
  }

  const { success, message, order } = await createOrderApi(
    selectedRoom.value.hash,
    selectedWeek,
    selectedTimeCode.value
  )
  if (success) {
    ecommerceAddToCart({
      products: [
        {
          id: order.room_id,
          name: selectedRoom.value.name,
          price: order.room_price,
          discountPrice: 0,
          vat: 0,
          quantity: 1,
        }
      ]
    })

    state.order = {
      ...order,
      expires_at: order.expires_at * 1000,
    }
    state.orderHash = order.hash
    state.orderServices = order.services
    if (order.payment_type) {
      setUserDetailValues({
        payment_type: order.payment_type,
      })
    } else {
      setUserDetailValues({})
    }

    state.amountServiceMinPrice = order.amount_service_min_price
    state.useYourOwnAmountServicePrice = order.use_your_own_amount_service_price
    state.isUseYourOwnAmountServiceDescription =
      order.use_your_own_amount_service_description
    state.useYourOwnAmountServiceHash = order.use_your_own_amount_service_hash

    state.canSelectUseYourOwnAmountService =
      order.can_select_use_your_own_amount_service
    state.useYourOwnAmountServiceAlreadyPaid =
      order.use_your_own_amount_service_already_paid

    if (order.use_your_own_amount_service_already_paid) {
      let mySelection = true

      let notMySelection = false

      order.services.forEach(({ id }) => {
        if (id !== -1 && id !== '-1') {
          notMySelection = true
        }
      })

      if (notMySelection) {
        mySelection = false
      }

      state.isUseYourOwnAmountService = mySelection
    } else {
      state.isUseYourOwnAmountService = order.is_use_your_own_amount_service
    }

    state.isAdmin = order.is_admin
    state.canEdit = order.can_edit

    state.canSelectCatering = order.can_select_catering

    if (history.pushState) {
      let newUrl =
        window.location.protocol +
        '//' +
        window.location.host +
        window.location.pathname
      if (newUrl.includes(':')) {
        newUrl = newUrl.split(':hash')[0]
      }

      newUrl = newUrl.replace(/\/$/, '')
      newUrl += `/:hash/${order.hash}`

      window.history.pushState({ path: newUrl }, '', newUrl)
    }

    return {
      success,
      message,
      order,
    }
  }

  return {
    success: false,
    message,
  }
}

export async function checkOrder() {
  if (state.orderHash) {
    const { success, message, order } = await orderApi(state.orderHash)

    if (!success && message) {
      setGlobalMessage(message)
    }

    if (success) {
      state.order = {
        ...order,
        expires_at: order.expires_at * 1000,
      }
      state.orderHash = order.hash
      state.orderServices = order.services
      state.paidServices = order.paid_services
      state.isEditMode = order.edit_mode
      state.isCancelRequested = order.cancel_requested
      state.amountServiceMinPrice = order.amount_service_min_price
      state.useYourOwnAmountServicePrice =
        order.use_your_own_amount_service_price
      state.isUseYourOwnAmountServiceDescription =
        order.use_your_own_amount_service_description
      state.useYourOwnAmountServiceHash = order.use_your_own_amount_service_hash

      state.canSelectUseYourOwnAmountService =
        order.can_select_use_your_own_amount_service
      state.useYourOwnAmountServiceAlreadyPaid =
        order.use_your_own_amount_service_already_paid

      if (order.use_your_own_amount_service_already_paid) {
        let mySelection = true

        let notMySelection = false

        order.services.forEach(({ id }) => {
          if (id !== -1 && id !== '-1') {
            notMySelection = true
          }
        })

        if (notMySelection) {
          mySelection = false
        }

        state.isUseYourOwnAmountService = mySelection
      } else {
        state.isUseYourOwnAmountService = order.is_use_your_own_amount_service
      }

      state.isAdmin = order.is_admin
      state.canEdit = order.can_edit

      setSelectedTimeCode(order.room_code)
      setUserDetailValues({ ...order.data, payment_type: order.payment_type })

      state.canSelectCatering = order.can_select_catering

      return {
        success,
        message,
      }
    }

    return {
      success,
      message,
    }
  }

  return {
    success: false,
  }
}

export async function adminBlockTime() {
  const {success, message} = await adminBlockTimeApi(state.orderHash)

  if (success && history.pushState) {
    let newUrl =
        window.location.protocol +
        '//' +
        window.location.host +
        window.location.pathname
    if (newUrl.includes(':')) {
      newUrl = newUrl.split(':hash')[0]
    }

    window.location.href = newUrl
  }

  return {
    success,
    message
  }
}

export async function addToBasket() {
  const { success, message } = await addToBasketApi(state.orderHash)

  if (success) {
    window.location.replace(window.settings.shop.seo.basketUrl)

    return {
      success,
    }
  }

  return {
    success,
    message,
  }
}

export const order = readonly(toRefs(state).order)
export const orderHash = readonly(toRefs(state).orderHash)
export const orderServices = readonly(toRefs(state).orderServices)
export const paidOrderServices = readonly(toRefs(state).paidServices)
export const orderEditMode = readonly(toRefs(state).isEditMode)
export const isCancelRequested = readonly(toRefs(state).isCancelRequested)
export const isUseYourOwnAmountServiceDescription = readonly(
  toRefs(state).isUseYourOwnAmountServiceDescription
)

export const amountServiceMinPrice = readonly(
  toRefs(state).amountServiceMinPrice
)
export const useYourOwnAmountServicePrice = readonly(
  toRefs(state).useYourOwnAmountServicePrice
)
export const useYourOwnAmountServiceHash = readonly(
  toRefs(state).useYourOwnAmountServiceHash
)
export const isUseYourOwnAmountService = readonly(
  toRefs(state).isUseYourOwnAmountService
)

export const canSelectUseYourOwnAmountService = readonly(
  toRefs(state).canSelectUseYourOwnAmountService
)

export const useYourOwnAmountServiceAlreadyPaid = readonly(
  toRefs(state).useYourOwnAmountServiceAlreadyPaid
)

export const setUseYourOwnAmountService = (selected) => {
  state.isUseYourOwnAmountService = selected
}

export const canEdit = computed(() => {
  if(!state.isEditMode){
    return true
  }

  return state.canEdit
})


export const isAdmin = readonly(toRefs(state).isAdmin)
export const canSelectCatering = readonly(toRefs(state).canSelectCatering)

function amountServicesTotalPrice() {
  if (state.isUseYourOwnAmountService) {
    return state.useYourOwnAmountServicePrice
  }

  let price = 0

  amountServices.value.forEach((service) => {
    if (service.selected) {
      price += service.price * service.selected_amount
    }
  })

  return price
}

function cateringServicesTotalPrice() {
  if (state.isUseYourOwnAmountService) {
    return state.useYourOwnAmountServicePrice
  }

  let selectedPrice = 0

  cateringServices.value.forEach(service => {
    if (service.selected) {
      if (service.items.length) {
        service.items.forEach(item => {
          if (item.id === service.selected_item_id) {
            selectedPrice += item.price * service.selected_amount
          }
        })
      } else {
        selectedPrice += service.price * service.selected_amount
      }
    }
  })


  return selectedPrice
}

function selectServicesTotalPrice() {
  let price = 0
  selectServices.value.forEach((service) => {
    if (service.selected) {
      service.items.forEach((item) => {
        if (item.selected) {
          price += item.price
        }
      })
    }
  })
  return price
}

export const totalPrice = computed(() => {
  let price = 0

  if (state.order.room_price) {
    price += state.order.room_price
  }

  let servicesPrice = 0

  if (state.isUseYourOwnAmountService) {
    servicesPrice = state.useYourOwnAmountServicePrice
  } else {
    servicesPrice += cateringServicesTotalPrice()
    servicesPrice += selectServicesTotalPrice()

    if (
      state.useYourOwnAmountServiceAlreadyPaid &&
      state.useYourOwnAmountServicePrice
    ) {
      servicesPrice = servicesPrice - state.useYourOwnAmountServicePrice

      if (servicesPrice < 0) {
        servicesPrice = 0
      }
    }
  }

  price += servicesPrice

  paidOrderServices.value.forEach((paidService) => {
    price += paidService.price * paidService.amount
  })

  return price
})

export const totalPayNowPrice = computed(() => {
  if (
    state.isEditMode &&
    state.useYourOwnAmountServiceAlreadyPaid &&
    state.isUseYourOwnAmountService
  ) {
    return 0
  }

  let price = 0

  if (!orderEditMode.value) {
    if (paymentType.value === 1) {
      price += order.value.room_part_price
    } else {
      price += order.value.room_price
    }
  }

  price += cateringServicesTotalPrice()
  price += selectServicesTotalPrice()

  if (
    state.useYourOwnAmountServiceAlreadyPaid &&
    state.useYourOwnAmountServicePrice
  ) {
    price = price - state.useYourOwnAmountServicePrice

    if (price < 0) {
      price = 0
    }
  }

  return price
})

export async function cancelOrder() {
  if (state.isEditMode) {
    const { success } = await cancelOrderApi(state.orderHash)

    if (!success) {
      return {
        success: false,
      }
    }
  }

  state.order = []
  state.orderHash = null
  state.orderServices = []
  setSelectedRoomId(null)
  setSelectedTimeCode(null)

  if(state.order && selectedRoom.value){
        ecommerceRemoveFromCart({
          products: [
            {
              id: state.order.room_id,
              name: selectedRoom.value.name,
              price: state.order.room_price,
              discountPrice: 0,
              vat: 0,
              quantity: 1,
            }
          ]
    })
  }

  if (history.pushState) {
    let newUrl =
      window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname
    if (newUrl.includes(':')) {
      newUrl = newUrl.split(':hash')[0]
    }

    window.history.pushState({ path: newUrl }, '', newUrl)
  }

  return {
    success: true,
  }
}

export const currency = window.settings.shop.currency
export const termsUrl = window.room_rent.shop_basket.terms_url
export const termsTitle = window.room_rent.shop_basket.terms_title
